import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import type { FirebaseOptions } from 'firebase/app';

// Validate and get Firebase configuration from environment variables only
const validateAndGetEnvVar = (key: string): string => {
  const value = import.meta.env[key];
  if (!value) {
    throw new Error(`Missing required environment variable: ${key}`);
  }
  return value;
};

const apiKey = validateAndGetEnvVar('VITE_FIREBASE_API_KEY');
const authDomain = validateAndGetEnvVar('VITE_FIREBASE_AUTH_DOMAIN');
const projectId = validateAndGetEnvVar('VITE_FIREBASE_PROJECT_ID');
const storageBucket = validateAndGetEnvVar('VITE_FIREBASE_STORAGE_BUCKET');
const messagingSenderId = validateAndGetEnvVar('VITE_FIREBASE_MESSAGING_SENDER_ID');
const appId = validateAndGetEnvVar('VITE_FIREBASE_APP_ID');
const measurementId = validateAndGetEnvVar('VITE_FIREBASE_MEASUREMENT_ID');

// Define Firebase configuration using environment variables with fallbacks
const firebaseConfig: FirebaseOptions = {
  apiKey,
  authDomain,
  projectId,
  storageBucket,
  messagingSenderId,
  appId,
  measurementId
};

// For debugging purposes
console.log("Initializing Firebase with config:", JSON.stringify({
  authDomain: firebaseConfig.authDomain,
  projectId: firebaseConfig.projectId,
  storageBucket: firebaseConfig.storageBucket,
  // Not logging full API key for security
  apiKey: firebaseConfig.apiKey ? "API_KEY_SET" : "API_KEY_MISSING"
}));

// Initialize Firebase
let app;
try {
  console.log("Attempting to initialize Firebase app...");
  app = initializeApp(firebaseConfig);
  console.log("Firebase initialization successful!");
} catch (error) {
  console.error("Firebase initialization failed:", error);
  throw new Error("Failed to initialize Firebase: " + (error instanceof Error ? error.message : String(error)));
}

// Initialize services
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);

// Verify auth initialization
console.log("Auth initialization check:", 
  auth ? "Auth initialized successfully" : "Auth initialization FAILED",
  "App name:", auth?.app?.name || "NO_APP_NAME"
);

// Development mode debugging
const isDevelopment = import.meta.env.DEV || import.meta.env.VITE_MODE === 'development';
const currentUrl = typeof window !== 'undefined' ? window.location.href : 'unknown';

console.log("Environment details:", {
  isDevelopment,
  currentUrl,
  mode: import.meta.env.MODE,
  dev: import.meta.env.DEV,
  viteMode: import.meta.env.VITE_MODE
});

if (isDevelopment) {
  console.log("Running in development mode - adding auth state debugging");
  
  // Add auth state change listener for debugging
  onAuthStateChanged(auth, (user) => {
    if (user) {
      console.log("🟢 Auth state: User logged in", {
        uid: user.uid,
        email: user.email,
        emailVerified: user.emailVerified
      });
    } else {
      console.log("🔴 Auth state: User logged out");
    }
  }, (error) => {
    console.error("❌ Auth state change error:", error);
  });
  
  // Add additional error handling for localhost issues
  auth.onIdTokenChanged((user) => {
    console.log("🔄 ID Token changed:", user ? "Token updated" : "No user");
  }, (error) => {
    console.error("❌ ID Token error:", error);
    console.log("This might indicate domain authorization issues with localhost");
  });
}

// Configure Firestore for better reliability
// Note: We no longer need to set experimentalForceLongPolling as it's handled differently in the new SDK
// Instead, we'll use proper error handling and reconnection logic in our service layer

// Export initialized services
export { auth, db, storage, app as firebase };

// Export types for better type safety across the application
export type { 
  Auth,
  User,
  UserCredential 
} from 'firebase/auth';

export type {
  Firestore,
  DocumentData,
  QuerySnapshot,
  DocumentSnapshot,
  DocumentReference,
  CollectionReference
} from 'firebase/firestore';

export type {
  StorageReference,
  UploadTaskSnapshot
} from 'firebase/storage';
